import { c as _c } from "react/compiler-runtime";
import { NO_TRANSLATE_CLASS } from "@flat-ui";
import { useDrawingArea } from "@mui/x-charts-pro/hooks";
import { PieArc, PieChart } from "@mui/x-charts-pro/PieChart";
import { getMarkupStatusLabel } from "@pages/project-details/project-markups/status/markup-status-utils";
import { getMarkupStatusChipColor } from "@pages/project-details/project-overview/markups-summary/markups-summary-colors";
import { PALETTE_VARS } from "@styles/palette-vars";
import { createContext, useContext } from "react";
import { useHref, useLinkClickHandler } from "react-router-dom";

/** Width and height of the chart in px */
export const MARKUPS_SUMMARY_CHART_SIZE = 200;

/** Inner radius of the ring, as a share of the chart radius */
const RING_INNER_RADIUS = 0.72;

/** Color of the overdue markups */
export const MARKUPS_OVERDUE_COLOR = PALETTE_VARS.error.main;

/** Color of the due soon markups */
export const MARKUPS_DUE_SOON_COLOR = PALETTE_VARS.warning.main;

/** A link of an arc, or undefined if the arc has no markups */

/** Links of the arcs, by series ID and data index. Provided through context since the arc slot only gets the arc props */
const ArcLinksContext = createContext({});

/** A pie arc and the table filters that show its markups */

/** Arcs of the chart, one per status */
function getStatusArcs(summary) {
  return summary.map(({
    status,
    count
  }) => ({
    arc: {
      id: status,
      value: count,
      label: getMarkupStatusLabel(status),
      color: getMarkupStatusChipColor(status)
    },
    filter: {
      statuses: [status]
    }
  }));
}

/** SVG link that navigates within the app on a plain click, and can still be opened in a new tab */
function SvgLink(t0) {
  const $ = _c(9);
  const {
    href,
    onClick,
    label,
    children
  } = t0;
  const resolvedHref = useHref(href);
  const handleClick = useLinkClickHandler(href);
  let t1;
  if ($[0] !== handleClick || $[1] !== onClick) {
    t1 = event => {
      onClick();
      handleClick(event);
    };
    $[0] = handleClick;
    $[1] = onClick;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      cursor: "pointer"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== label || $[6] !== resolvedHref || $[7] !== t1) {
    t3 = <a href={resolvedHref} onClick={t1} aria-label={label} style={t2}>{children}</a>;
    $[4] = children;
    $[5] = label;
    $[6] = resolvedHref;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/** Pie arc wrapped in a link to the annotations tab, if it has markups */
function LinkedPieArc(props) {
  const $ = _c(7);
  const link = useContext(ArcLinksContext)[props.seriesId]?.[props.dataIndex];
  if (!link) {
    let t0;
    if ($[0] !== props) {
      t0 = <PieArc {...props} />;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <PieArc {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== link || $[5] !== t0) {
    t1 = <SvgLink {...link}>{t0}</SvgLink>;
    $[4] = link;
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

/** Gray ring shown instead of the arcs when there are no markups, since the chart draws nothing for them */
function EmptyRing() {
  const $ = _c(5);
  const {
    left,
    top,
    width,
    height
  } = useDrawingArea();
  const radius = Math.min(width, height) / 2;
  const thickness = radius * (1 - RING_INNER_RADIUS);
  const t0 = left + width / 2;
  const t1 = top + height / 2;
  const t2 = radius - thickness / 2;
  let t3;
  if ($[0] !== t0 || $[1] !== t1 || $[2] !== t2 || $[3] !== thickness) {
    t3 = <circle cx={t0} cy={t1} r={t2} fill="none" stroke={PALETTE_VARS.divider} strokeWidth={thickness} />;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = thickness;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/** Shows the total amount of markups in the center of the chart, linking to all of them */
function MarkupsSummaryCenterLabel(t0) {
  const $ = _c(17);
  const {
    total,
    centerLabel,
    getLink
  } = t0;
  const {
    left,
    top,
    width,
    height
  } = useDrawingArea();
  const centerX = left + width / 2;
  const centerY = top + height / 2;
  let t1;
  if ($[0] !== getLink) {
    t1 = getLink({
      statuses: []
    }, "chart");
    $[0] = getLink;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${centerLabel}: ${total}`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      fill: PALETTE_VARS.text.primary,
      fontSize: "1.75rem",
      fontWeight: 600
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== centerX || $[4] !== centerY || $[5] !== total) {
    t4 = <text className={NO_TRANSLATE_CLASS} x={centerX} y={centerY} textAnchor="middle" style={t3}>{total}</text>;
    $[3] = centerX;
    $[4] = centerY;
    $[5] = total;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      fill: PALETTE_VARS.text.secondary,
      fontSize: "0.75rem"
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== centerLabel || $[9] !== centerX || $[10] !== centerY) {
    t6 = <text x={centerX} y={centerY} dy="1.5em" textAnchor="middle" style={t5}>{centerLabel}</text>;
    $[8] = centerLabel;
    $[9] = centerX;
    $[10] = centerY;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t4 || $[15] !== t6) {
    t7 = <SvgLink {...t1} label={t2}>{t4}{t6}</SvgLink>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/** Donut chart with the markups per status */
export function MarkupsSummaryChart(t0) {
  const $ = _c(42);
  const {
    summary,
    total,
    centerLabel,
    getLink,
    size: t1
  } = t0;
  const size = t1 === undefined ? MARKUPS_SUMMARY_CHART_SIZE : t1;
  let T0;
  let T1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== getLink || $[1] !== size || $[2] !== summary) {
    const statusArcs = getStatusArcs(summary);
    let t11;
    if ($[14] !== getLink) {
      t11 = function getArcLinks(arcs) {
        return arcs.map(t12 => {
          const {
            arc,
            filter
          } = t12;
          return arc.value > 0 && filter ? {
            ...getLink(filter, "chart"),
            label: `${arc.label}: ${arc.value}`
          } : undefined;
        });
      };
      $[14] = getLink;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    const getArcLinks = t11;
    T1 = ArcLinksContext.Provider;
    t10 = {
      status: getArcLinks(statusArcs)
    };
    T0 = PieChart;
    t4 = size;
    t5 = size;
    t6 = 0;
    t7 = true;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        flex: "none"
      };
      t9 = {
        pieArc: LinkedPieArc
      };
      $[16] = t8;
      $[17] = t9;
    } else {
      t8 = $[16];
      t9 = $[17];
    }
    t2 = "status";
    t3 = statusArcs.map(_temp);
    $[0] = getLink;
    $[1] = size;
    $[2] = summary;
    $[3] = T0;
    $[4] = T1;
    $[5] = t10;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    T0 = $[3];
    T1 = $[4];
    t10 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[18] !== t2 || $[19] !== t3) {
    t11 = [{
      id: t2,
      data: t3,
      innerRadius: `${RING_INNER_RADIUS * 100}%`,
      outerRadius: "100%"
    }];
    $[18] = t2;
    $[19] = t3;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== total) {
    t12 = !total && <EmptyRing />;
    $[21] = total;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== centerLabel || $[24] !== getLink || $[25] !== total) {
    t13 = <MarkupsSummaryCenterLabel total={total} centerLabel={centerLabel} getLink={getLink} />;
    $[23] = centerLabel;
    $[24] = getLink;
    $[25] = total;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== T0 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t4 || $[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
    t14 = <T0 width={t4} height={t5} margin={t6} hideLegend={t7} sx={t8} slots={t9} series={t11}>{t12}{t13}</T0>;
    $[27] = T0;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== T1 || $[39] !== t10 || $[40] !== t14) {
    t15 = <T1 value={t10}>{t14}</T1>;
    $[38] = T1;
    $[39] = t10;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
}
function _temp(t0) {
  const {
    arc: arc_0
  } = t0;
  return arc_0;
}